import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardBody, Alert, Spinner } from '@kollektive/design-system';
import { supabase } from '@/lib/supabase';
import styles from './RegisterPage.module.css';

// Session storage key for registration flow
export const REGISTRATION_FLOW_KEY = 'auth_registration_flow';

// Google icon SVG
function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

// Microsoft icon SVG
function MicrosoftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

export function RegisterPage() {
  const [isSigningUp, setIsSigningUp] = useState<'google' | 'microsoft' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleGoogleSignUp = async () => {
    setIsSigningUp('google');
    setError(null);

    // Store registration flow flag
    sessionStorage.setItem(REGISTRATION_FLOW_KEY, 'true');

    const redirectTo = `${window.location.origin}/auth/callback?flow=register`;

    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo },
    });

    if (oauthError) {
      setError(oauthError.message);
      sessionStorage.removeItem(REGISTRATION_FLOW_KEY);
    }
    setIsSigningUp(null);
  };

  const handleMicrosoftSignUp = async () => {
    setIsSigningUp('microsoft');
    setError(null);

    // Store registration flow flag
    sessionStorage.setItem(REGISTRATION_FLOW_KEY, 'true');

    const redirectTo = `${window.location.origin}/auth/callback?flow=register`;

    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: {
        redirectTo,
        scopes: 'email profile openid',
      },
    });

    if (oauthError) {
      setError(oauthError.message);
      sessionStorage.removeItem(REGISTRATION_FLOW_KEY);
    }
    setIsSigningUp(null);
  };

  const isButtonDisabled = isSigningUp !== null;

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {/* Logo and branding */}
        <div className={styles.branding}>
          <div className={styles.logoPlaceholder}>
            <span>K</span>
          </div>
          <h1 className={styles.title}>Create your workspace</h1>
          <p className={styles.subtitle}>
            Get started with Kollektive in seconds. No credit card required.
          </p>
        </div>

        {/* Registration card */}
        <Card>
          <CardBody className={styles.cardBody}>
            {/* Error message */}
            {error && (
              <Alert variant="error" styleVariant="glass" className={styles.error}>
                {error}
              </Alert>
            )}

            {/* OAuth buttons */}
            <div className={styles.oauthButtons}>
              <button
                onClick={handleGoogleSignUp}
                disabled={isButtonDisabled}
                className={styles.googleButton}
              >
                {isSigningUp === 'google' ? (
                  <Spinner size="sm" />
                ) : (
                  <GoogleIcon className={styles.oauthIcon} />
                )}
                <span>Sign up with Google</span>
              </button>

              <button
                onClick={handleMicrosoftSignUp}
                disabled={isButtonDisabled}
                className={styles.microsoftButton}
              >
                {isSigningUp === 'microsoft' ? (
                  <Spinner size="sm" />
                ) : (
                  <MicrosoftIcon className={styles.oauthIcon} />
                )}
                <span>Sign up with Microsoft</span>
              </button>
            </div>

            {/* Divider */}
            <div className={styles.divider}>
              <div className={styles.dividerLine} />
              <span className={styles.dividerText}>Secure authentication</span>
              <div className={styles.dividerLine} />
            </div>

            {/* Footer text */}
            <p className={styles.terms}>
              By signing up, you agree to our terms of service and privacy policy.
            </p>
          </CardBody>
        </Card>

        {/* Sign in link */}
        <p className={styles.signInLink}>
          Already have an account?{' '}
          <Link to="/login" className={styles.link}>
            Sign in
          </Link>
        </p>

        {/* Powered by */}
        <p className={styles.poweredBy}>
          Powered by <span className={styles.poweredByBrand}>Kollektive</span>
        </p>
      </div>
    </div>
  );
}
